import { useState, useEffect, useContext } from "react";
import { ThemeContext } from "../../../constant/theme";
import { LanguageContext } from "../../../constant/LanguageContext";
import { translations } from "../constants/translations";
import { apiFetch } from "../../../context/AuthContext";
import { clearAuthStorage } from "../../../utils/authStorage";
import { sessionManager } from "../../../utils/sessionManager";
import { isPasswordValid } from "../../../utils/passwordPolicy";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export const useSettingsState = ({ onLogout } = {}) => {
  // ← accept onLogout
  const { theme, setTheme } = useContext(ThemeContext);
  const { language, toggleLanguage } = useContext(LanguageContext);

  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    sms: false,
    desktop: true,
  });

  const [privacy, setPrivacy] = useState({
    profileVisibility: "public",
    dataSharing: false,
    analyticsTracking: true,
  });

  const [display, setDisplay] = useState({
    fontSize: "medium",
    density: "comfortable",
    animations: true,
  });

  const [security, setSecurity] = useState({
    twoFactorAuth: false,
    sessionTimeout: "30",
    passwordStrength: "strong",
  });

  const [savedStatus, setSavedStatus] = useState(false);
  const [showPasswordSettings, setShowPasswordSettings] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768,
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSavedStatus(true);
      setTimeout(() => setSavedStatus(false), 2000);
    }, 1000);
    return () => clearTimeout(timer);
  }, [language, theme, notifications, privacy, display, security]);

  const handleNotificationChange = (type, value) =>
    setNotifications((prev) => ({ ...prev, [type]: value }));

  const handlePrivacyChange = (type, value) =>
    setPrivacy((prev) => ({ ...prev, [type]: value }));

  const handleDisplayChange = (type, value) =>
    setDisplay((prev) => ({ ...prev, [type]: value }));

  const handleSecurityChange = (type, value) =>
    setSecurity((prev) => ({ ...prev, [type]: value }));

  const handleLogout = () => setShowLogoutModal(true);

  const confirmLogout = async () => {
    // ← now async
    setShowLogoutModal(false);
    if (onLogout) {
      await onLogout(); // ← calls useLogout's logout()
    } else {
      // fallback just in case — same teardown as every other logout path
      // (keeps the device's theme/language preferences).
      clearAuthStorage();
      sessionManager.logout();
      window.location.href = "/login";
    }
  };

  const [passwordFields, setPasswordFields] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handlePasswordFieldChange = (field, value) => {
    setPasswordFields((prev) => ({ ...prev, [field]: value }));
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handleChangePassword = async (e) => {
    if (e) e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    const { oldPassword, newPassword, confirmPassword } = passwordFields;
    const pt = translations[language] || translations.en;

    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordError(pt.privacy.allFieldsRequired);
      return;
    }

    // Same rules the emailed activation/reset link enforces — see
    // utils/passwordPolicy.
    if (!isPasswordValid(newPassword)) {
      setPasswordError(pt.privacy.passwordPolicyNotMet);
      return;
    }

    if (newPassword === oldPassword) {
      setPasswordError(pt.privacy.passwordSameAsOld);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(pt.privacy.passwordsDoNotMatch);
      return;
    }

    setPasswordLoading(true);

    try {
      const response = await apiFetch(`${BASE_URL}/users/change-password`, {
        method: "POST",
        body: JSON.stringify({ oldPassword, newPassword }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setPasswordSuccess(pt.privacy.passwordSuccessfullyChanged);
        setPasswordFields({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setTimeout(() => {
          setPasswordSuccess("");
          setShowPasswordSettings(false);
        }, 2000);
      } else {
        setPasswordError(
          data.error ||
            (language === "ar"
              ? "فشل تغيير كلمة المرور. يرجى التحقق من كلمة المرور القديمة"
              : "Failed to change password. Please check your old password")
        );
      }
    } catch (err) {
      console.error("Change password error:", err);
      setPasswordError(
        language === "ar"
          ? "حدث خطأ في الاتصال بالخادم"
          : "A network error occurred while communicating with the server"
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const t = translations[language] || translations.en;
  const isRTL = language === "ar";

  return {
    // theme & language
    theme,
    setTheme,
    language,
    toggleLanguage,
    t,
    isRTL,
    isMobile,

    // states
    notifications,
    privacy,
    display,
    security,
    savedStatus,
    showPasswordSettings,
    setShowPasswordSettings,
    showLogoutModal,
    setShowLogoutModal,

    // password change states & handlers
    passwordFields,
    passwordLoading,
    passwordError,
    passwordSuccess,
    showOldPassword,
    setShowOldPassword,
    showNewPassword,
    setShowNewPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    handlePasswordFieldChange,
    handleChangePassword,

    // handlers
    handleNotificationChange,
    handlePrivacyChange,
    handleDisplayChange,
    handleSecurityChange,
    handleLogout,
    confirmLogout,
  };
};
